import * as THREE from 'three'

const col = new THREE.Color()
const hexToRgb = hex => {
  col.set(hex)
  return [col.r, col.g, col.b]
}

export class ClientLights {
  constructor(client) {
    this.client = client
    this.maxLights = 128
    this.lights = new Map()
    this.nextId = 0
    this.dirty = true
    this._sortedBuf = []
    this.activeMax = 32

    const texW = this.maxLights * 2
    this.dataArray = new Float32Array(texW * 4)
    this.dataTex = new THREE.DataTexture(this.dataArray, texW, 1, THREE.RGBAFormat, THREE.FloatType)
    this.dataTex.minFilter = THREE.NearestFilter
    this.dataTex.magFilter = THREE.NearestFilter
    this.dataTex.generateMipmaps = false
    this.dataTex.colorSpace = THREE.LinearSRGBColorSpace
    this.dataTex.needsUpdate = true

    const a = new THREE.Color().setStyle('rgb(31, 26, 64)')
    this.ambient = new THREE.Vector3(a.r, a.g, a.b)
    this.darkness = 0.0

    this.uniforms = {
      uLightData: { value: this.dataTex },
      uLightCount: { value: 0 },
      uMaxLights: { value: this.maxLights },
      uAmbient: { value: this.ambient },
      uDarkness: { value: this.darkness },
    }

    this.UNIFORMS_GLSL = `
      uniform sampler2D uLightData;
      uniform int uLightCount;
      uniform int uMaxLights;
      uniform vec3 uAmbient;
      uniform float uDarkness;`

    this.FUNCTION_GLSL = `
      vec3 applyLighting(vec3 color, vec2 worldPos) {
        if (uDarkness < 0.001) return color;
        vec3 lightAccum = vec3(0.0);
        float texWidth = float(uMaxLights) * 2.0;
        for (int i = 0; i < ${this.activeMax}; i++) {
          if (i >= uLightCount) break;
          float u0 = (float(i) * 2.0 + 0.5) / texWidth;
          vec4 d0 = texture2D(uLightData, vec2(u0, 0.5));
          float u1 = (float(i) * 2.0 + 1.5) / texWidth;
          vec4 d1 = texture2D(uLightData, vec2(u1, 0.5));
          float dist = distance(worldPos, d0.xy);
          float atten = clamp(1.0 - dist / d0.z, 0.0, 1.0);
          atten *= atten;
          lightAccum += d1.rgb * d0.w * atten;
        }
        vec3 totalLight = min(uAmbient + lightAccum, vec3(3.0));
        return mix(color, color * totalLight, uDarkness);
      }`
  }

  add(x, y, radius = 1, color = '#ffffff', intensity = 1) {
    const id = this.nextId++
    const self = this
    const light = {
      id,
      x,
      y,
      radius,
      color: hexToRgb(color),
      intensity,
      setPosition(nx, ny) {
        this.x = nx
        this.y = ny
        self.dirty = true
      },
      setRadius(nr) {
        this.radius = nr
        self.dirty = true
      },
      setColor(nextColor) {
        this.color = hexToRgb(nextColor)
        self.dirty = true
      },
      setIntensity(ni) {
        this.intensity = ni
        self.dirty = true
      },
      destroy() {
        self.lights.delete(this.id)
        self.dirty = true
      },
    }
    this.lights.set(id, light)
    this.dirty = true
    return light
  }

  setAmbient(r, g, b) {
    this.ambient.set(r, g, b)
  }

  setDarkness(v) {
    this.darkness = Math.max(0, Math.min(1, v))
    this.uniforms.uDarkness.value = this.darkness
  }

  update() {
    const cam = this.client.camera
    const cx = cam.position.x
    const cy = cam.position.y
    const buf = this._sortedBuf
    buf.length = 0
    for (const l of this.lights.values()) {
      const dx = l.x - cx
      const dy = l.y - cy
      l._dist = dx * dx + dy * dy
      buf.push(l)
    }

    buf.sort((a, b) => a._dist - b._dist)

    const count = Math.min(buf.length, this.activeMax)
    this.uniforms.uLightCount.value = count

    const arr = this.dataArray
    arr.fill(0)
    for (let i = 0; i < count; i++) {
      const l = buf[i]
      const base = i * 2 * 4
      arr[base] = l.x
      arr[base + 1] = l.y
      arr[base + 2] = l.radius
      arr[base + 3] = l.intensity
      arr[base + 4] = l.color[0]
      arr[base + 5] = l.color[1]
      arr[base + 6] = l.color[2]
      arr[base + 7] = 1.0
    }
    this.dataTex.needsUpdate = true
  }
}
